// Copyright (c) Move Closer 2022.

import {
  ApiConnectorType,
  AppModule,
  Container,
  IConfiguration,
  IConnector,
  IModal,
  Interfaces,
  ModalType,
  Module
} from '@movecloser/front-core'

import { DrawerType, IDrawer } from '../shared/contracts/services'

import { COMPONENTS_STRUCTURE_CONFIG, logger } from '../../support'
import { ComponentsStructureConfig } from '../../support/mixins'
import { GraphQlConnectorType, IGraphQL, RelatedServiceType } from '../../contexts'

import { drawersRegistry, modalsRegistry } from './config/modals'
import {
  IProductReviewsRepository,
  IProductsRepository,
  IProductStockRepository,
  ProductsRepositoryType,
  ProductStockRepositoryType,
  ReviewsRepositoryType
} from './contracts/repositories'

import { IProfileTabsService, ProfileTabsServiceType } from '../profile/services/profile-tabs'

import { ISearchService, SearchServiceType } from '../shared/services/search'
import { ProductsRepository } from './repositories/products'
import { routesFactory as routes } from './routes'
import { resources } from './config/resources'
import { searchResultConfig } from './config/search'
import { ReviewsRepository } from './repositories/reviews'
import { reviewsQueries } from './config/queries'
import { pickTabItemsFactory } from '../shared/support/tabs.factory'
import { ProductStockRepository } from './repositories/stock'
import { stockQueries } from './config/queries/stock.queries'
import { storeFactory as state } from './store'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
@AppModule({
  name: 'products',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  providers: (config: IConfiguration) => {
    return (bind: Interfaces.Bind) => {
      bind<IProductReviewsRepository>(ReviewsRepositoryType).toDynamicValue((context) => {
        return new ReviewsRepository(context.container.get(GraphQlConnectorType))
      }).inSingletonScope()

      bind<IProductsRepository>(ProductsRepositoryType).toDynamicValue(context => {
        return new ProductsRepository(
          context.container.get(ApiConnectorType),
          context.container.get(RelatedServiceType)
        )
      })

      bind<IProductStockRepository>(ProductStockRepositoryType).toDynamicValue(context => {
        return new ProductStockRepository(
          context.container.get<IGraphQL>(GraphQlConnectorType)
        )
      })
    }
  },
  boot (container: Container, config: IConfiguration) {
    const configuration = config.byKey<ComponentsStructureConfig>(COMPONENTS_STRUCTURE_CONFIG)

    const connector: IConnector = container.get(ApiConnectorType)
    connector.useResources(resources)

    const modalConnector = container.get<IModal>(ModalType)
    modalConnector.register(modalsRegistry)

    const drawerConnector = container.get<IDrawer>(DrawerType)
    drawerConnector.register(drawersRegistry)

    // Register product search results config.
    const searchService = container.get<ISearchService | undefined>(SearchServiceType)
    if (searchService) {
      searchService.addTabs(searchResultConfig)
    } else {
      logger('[ProductsModule]: There\'s no SearchService available', 'debug')
    }

    // Create profile tabs
    const profileTabsService = container.get<IProfileTabsService | undefined>(ProfileTabsServiceType)
    if (profileTabsService) {
      profileTabsService.addTab(pickTabItemsFactory(configuration, 'product'))
    } else {
      logger('[ProductsModule]: There\'s no ProfileTabsService available', 'debug')
    }

    const graph = container.get<IGraphQL>(GraphQlConnectorType)
    graph.useQueries(reviewsQueries(config))
    graph.useQueries(stockQueries(config))
  },
  routes,
  state
})
export class ProductsModule extends Module {}
